import {
  LocalizationContext,
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import styles from "./invisalign.module.css";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { Link } from "~/components/RouterLink";
import type { ReactNode } from "react";
import { useContext } from "react";
import { OptimizedImage } from "~/components/OptimizedImage";

type Localization = {
  title: string;
  heading: string;
  // Invisalign Basics Section
  basicsTitle: string;
  basicsP1: string;
  basicsP2: string;
  basicsP3: string;
  // Cost Section
  costTitle: string;
  costP1: string;
  costP2: string;
  // Affordable Treatment Section
  affordableTitle: string;
  affordableContent: string;
  // Payment Options Section
  paymentTitle: string;
  paymentList: string[];
  // Process Section
  processTitle: string;
  processP1: string;
  processP2: string;
  processP3: string;
  processP4: string;
  processP5: string;
  processP6: string;
  processP7: string;
  // Differences Section
  differencesTitle: string;
  differencesP1: string;
  differencesP2: string;
  differencesP3: string;
  // Adults Section
  adultsTitle: string;
  adultsP1: string;
  adultsP2: string;
  adultsP3: string;
  adultsP4: string;
  adultsP5: string;
  straightenTitle: string;
  straightenList: string[];
  straightenP1: string;
  straightenP2: string;
  whyIntro: string;
  whyTitle: string;
  whyList: string[];
  responsibilitiesTitle: string;
  responsibilitiesP1: string;
  responsibilitiesP2: string;
  responsibilitiesP3: string;
  responsibilitiesP4: string;
};

const englishLocalization: Localization = {
  title: "Invisalign",
  heading: "Invisalign® Basics",

  basicsTitle: "Invisalign® Basics",
  basicsP1:
    "Invisalign® is a teeth-straightening system that uses a custom-made series of aligners designed specifically for each patient that can be used to straighten teeth without the use of metal brackets or wires.",
  basicsP2:
    "When you choose Miami Orthodontic Specialists as your Invisalign provider, Dr. Garcia-Paul will take impressions of your teeth and then work with 3-D computer-based software to plan out your tooth movement. Invisalign is an esthetic alternative to traditional orthodontic treatment methods.",
  basicsP3:
    "Call to schedule your first free consultation appointment today to see if Invisalign aligners can help you perfect your smile.",
  costTitle: "How Much Does Invisalign Cost?",
  costP1:
    "Several factors determine the cost of Invisalign treatment at Miami Orthodontic Specialists. In most cases, we do not charge more for Invisalign treatment than we would for braces or another treatment type. The exact treatment cost depends on the problems with the teeth and bite and how long the treatment is going to take.",
  costP2:
    "This is why we offer a free initial consultation – so that you can be evaluated by Dr. Garcia-Paul and we can explain the treatment that will help you achieve your dream smile.",
  affordableTitle: "Affordable Invisalign Treatment",
  affordableContent:
    "At Miami Orthodontic Specialists, we do everything possible to make Invisalign an affordable treatment option. We know that straight teeth are an investment in your health and in your future.",
  paymentTitle: "Payment Options for Invisalign",
  paymentList: [
    "An in-house payment plan with the cost divided over the months of treatment to fit your budget",
    "Low monthly payments",
    "Option to use Health Savings Account or Flexible Spending Account",
    "Discount on paid-in-full treatment",
  ],
  processTitle: "The Invisalign Process",
  processP1:
    "We'll take photos and X-rays to create a 3D image of your mouth. We make your first set of custom-fitted aligners from these images, and then you begin the process of straightening your teeth.",
  processP2:
    "You will probably wear Invisalign aligners for 9–15 months, and will typically use 18–30 sets during your treatment.",
  processP3:
    "You wear the trays for about 22 hours a day but can remove them for brushing your teeth and eating. Based on your individual needs, you will wear several sets of trays throughout your orthodontic treatment, and will start to notice a change in just a few months!",
  processP4:
    "Invisalign® takes a modern approach to straightening teeth. It uses a custom-made series of aligners created for you and only you. These aligner trays are made of smooth, comfortable and virtually invisible plastic that you wear over your teeth.",
  processP5:
    "Wearing the aligners will gently move your teeth into place little by little, based on the exact movements your orthodontist plans for you. There are no metal brackets to attach and no wires to tighten. You just pop in a new set of aligners about every two weeks, until your treatment is complete.",
  processP6:
    "You’ll get a great smile without a treatment that gets in the way of your daily life. The best part about the whole process is that most people won’t even know you’re straightening your teeth. See more at the Invisalign site.",
  processP7:
    "This nearly-invisible option to straighten your teeth is truly changing the way people see orthodontic treatment. Dr. Garcia-Paul has a lot of experience treating patients with Invisalign, and we’re excited to offer this technology to our Miami, FL braces patients.",
  differencesTitle: "Differences Between Invisalign & Braces",
  differencesP1:
    "Unlike traditional braces, smooth plastic Invisalign aligners don't have metal components that can irritate your mouth. Because they’re removed when you eat, you won't have to watch what you eat like you would when wearing braces—bring on the popcorn and peanuts.",
  differencesP2:
    "In fact, the aligners must be removed for eating and for drinking anything other than water. You need to make sure you’re willing to do that before you decide on Invisalign clear braces. To prevent staining, you’ll need to clean your teeth before putting the aligners back on after each time you eat.",
  differencesP3:
    "Of course, brushing and flossing are much easier than with traditional braces, since you don’t have to work around any brackets and wires.",
  adultsTitle: "Invisalign® For Adults",
  adultsP1: "We are proud to be the Miami Invisalign Preferred Provider!",
  adultsP2: `How would you like to get the beautiful, straight teeth that you’ve always wanted… without braces?`,
  adultsP3: `The terms “Metal Mouth,” “Brace Face” and “Tin Grin” may come to mind when you think of orthodontic treatment. The amount of metal that was previously used to straighten teeth discouraged many adults from considering orthodontic treatment in the past.`,
  adultsP4:
    "Newer advances, like Invisalign® have made it possible for adults to achieve the straight smile that they’ve been wanting without flashing a grill of metal every time they smile. Better still, advances in orthodontics have allowed treatment to become faster and more comfortable, permitting patients to finish their treatment much more quickly.",
  adultsP5:
    "Today, over 25% of patients in the United States are adults, but in our practice, the ratio is actually closer to 50:50. Our patients range in age from 7 to 77. Some adults come in seeking treatment for the first time and others want to fix teeth that have shifted over time. Regardless of your reason, we are here to help you obtain the beautiful, straight teeth that will keep you smiling for years to come.",
  straightenTitle: "Why Straighten Your Teeth?",
  straightenList: [
    "A more confident smile",
    "Improved self-image",
    "Straight teeth are easier to keep clean",
    "Less chance of developing gum disease, bad breath, tooth wear & potential tooth loss",
  ],
  straightenP1:
    "From a health perspective, straight teeth are easier to keep clean than crowded and overlapping teeth-decreasing the chances of developing gum disease, bad breath, tooth wear and potential tooth loss.",
  straightenP2:
    "From a cosmetic point of view, straight teeth can lead to a more confident smile and an improved self-image, which can also lead to improved performance at work or school. Regardless of the reason, all treatment options are presented at the first appointment, and you get to decide what works best for you.",
  whyIntro: "Many adults choose Invisalign because…",
  whyTitle: "Why Choose Invisalign?",
  whyList: [
    "It is more cosmetic – not visible in photos",
    "No food restrictions – you remove them to eat",
    "Easier to clean – brushing & flossing is not an issue",
    "More comfortable – no wires or braces",
  ],
  responsibilitiesTitle: "Responsibilities of Invisalign",
  responsibilitiesP1:
    "It’s important to wear your aligners for at least 22 hours each day for them to do their job and straighten your teeth. But, with Invisalign, you can remove your aligners for special occasions.",
  responsibilitiesP2:
    "Like with traditional braces, you will generally need to wear a retainer after Invisalign treatment to maintain your new alignment—and your confident new smile.",
  responsibilitiesP3:
    "Invisalign may not be recommended for some complex alignment or bite issues, or for those who have bridgework; the doctor can advise you about what options are best for you.",
  responsibilitiesP4:
    "Schedule your free consultation at our Miami, FL office to learn if Invisalign is right for you.",
};

const spanishLocalization: Localization = {
  title: "Invisalign",
  heading: "Conceptos básicos de Invisalign®",

  basicsTitle: "Conceptos básicos de Invisalign®",
  basicsP1:
    "Invisalign® es un sistema de enderezamiento dental que utiliza una serie de alineadores hechos a medida diseñados específicamente para cada paciente que se pueden usar para enderezar los dientes sin el uso de brackets o alambres metálicos.",
  basicsP2:
    "Cuando eliges a Miami Orthodontic Specialists como tu proveedor de Invisalign, la Dra. García-Paul tomará impresiones de tus dientes y luego trabajará con un software computarizado 3D para planificar el movimiento dental. Invisalign es una alternativa estética a los métodos tradicionales de tratamiento de ortodoncia.",
  basicsP3:
    "Llama para programar tu primera consulta gratuita hoy y saber si los alineadores Invisalign pueden ayudarte a perfeccionar tu sonrisa.",
  costTitle: "¿Cuánto cuesta Invisalign?",
  costP1:
    "Varios factores determinan el costo del tratamiento de Invisalign en Miami Orthodontic Specialists. En la mayoría de los casos, no cobramos más por el tratamiento de Invisalign que por los brackets u otro tipo de tratamiento. El costo exacto del tratamiento depende de los problemas con los dientes y la mordida, y de cuánto tiempo tomará el tratamiento.",
  costP2:
    "Por eso ofrecemos una consulta inicial gratuita, para que la Dra. García-Paul pueda evaluarte y podamos explicarte el tratamiento que te ayudará a lograr la sonrisa de tus sueños.",
  affordableTitle: "Tratamiento Invisalign Asequible",
  affordableContent:
    "En Miami Orthodontic Specialists, hacemos todo lo posible para que Invisalign sea una opción de tratamiento asequible. Sabemos que unos dientes alineados son una inversión en tu salud y en tu futuro.",
  paymentTitle: "Opciones de pago para Invisalign",
  paymentList: [
    "Un plan de pago interno con el costo dividido durante los meses de tratamiento para ajustarse a tu presupuesto",
    "Pagos mensuales bajos",
    "Opción de usar una cuenta de ahorros para la salud o una cuenta de gastos flexibles",
    "Descuento en el tratamiento pagado en su totalidad",
  ],
  processTitle: "El Proceso de Invisalign",
  processP1:
    "Tomaremos fotos y radiografías para crear una imagen 3D de tu boca. Hacemos tu primer juego de alineadores personalizados a partir de estas imágenes, y luego comienza el proceso de enderezar tus dientes.",
  processP2:
    "Probablemente usarás los alineadores Invisalign durante 9–15 meses y, por lo general, usarás 18–30 juegos durante tu tratamiento.",
  processP3:
    "Usarás las bandejas aproximadamente 22 horas al día, pero puedes quitártelas para cepillarte los dientes y comer. Según tus necesidades individuales, usarás varios juegos de bandejas durante tu tratamiento de ortodoncia, y comenzarás a notar un cambio en solo unos meses.",
  processP4:
    "Invisalign® adopta un enfoque moderno para enderezar los dientes. Utiliza una serie de alineadores hechos a medida creados para ti y solo para ti. Estas bandejas de alineación están hechas de plástico suave, cómodo y prácticamente invisible que usas sobre los dientes.",
  processP5:
    "Usar los alineadores moverá suavemente tus dientes a su lugar poco a poco, según los movimientos exactos que tu ortodoncista planifique para ti. No hay brackets metálicos que colocar ni alambres que ajustar. Simplemente colocas un nuevo juego de alineadores aproximadamente cada dos semanas, hasta completar tu tratamiento.",
  processP6:
    "Obtendrás una gran sonrisa sin un tratamiento que interfiera con tu vida diaria. Lo mejor de todo el proceso es que la mayoría de las personas ni siquiera sabrán que estás enderezando tus dientes. Obtén más información en el sitio de Invisalign.",
  processP7:
    "Esta opción casi invisible para enderezar los dientes está cambiando la manera en que las personas ven el tratamiento de ortodoncia. La Dra. García-Paul tiene mucha experiencia tratando pacientes con Invisalign, y nos entusiasma ofrecer esta tecnología a nuestros pacientes de brackets en Miami, FL.",
  differencesTitle: "Diferencias Entre Invisalign y Brackets",
  differencesP1:
    "A diferencia de los brackets tradicionales, los alineadores de plástico suave Invisalign no tienen componentes metálicos que puedan irritar tu boca. Como se retiran al comer, no tendrás que cuidar lo que comes como lo harías con brackets: que vengan las palomitas de maíz y los cacahuates.",
  differencesP2:
    "De hecho, los alineadores deben retirarse para comer y para beber cualquier cosa que no sea agua. Debes asegurarte de estar dispuesto a hacerlo antes de decidirte por los brackets transparentes Invisalign. Para evitar manchas, deberás limpiar tus dientes antes de volver a colocar los alineadores después de cada comida.",
  differencesP3:
    "Por supuesto, cepillarte y usar hilo dental es mucho más fácil que con los brackets tradicionales, ya que no tienes que trabajar alrededor de brackets y alambres.",
  adultsTitle: "Invisalign® Para Adultos",
  adultsP1:
    "¡Nos enorgullece ser el Proveedor Preferido de Invisalign en Miami!",
  adultsP2: `¿Te gustaría tener los dientes hermosos y derechos que siempre has querido… sin brackets?`,
  adultsP3: `Los términos “boca de metal”, “cara de brackets” y “sonrisa de lata” pueden venir a la mente cuando piensas en el tratamiento de ortodoncia. La cantidad de metal que se usaba antes para enderezar los dientes desalentó a muchos adultos de considerar un tratamiento de ortodoncia en el pasado.`,
  adultsP4:
    "Los avances más recientes, como Invisalign®, han hecho posible que los adultos logren la sonrisa derecha que han estado deseando sin mostrar metal cada vez que sonríen. Mejor aún, los avances en ortodoncia han permitido que el tratamiento sea más rápido y cómodo, lo que permite a los pacientes terminarlo mucho más pronto.",
  adultsP5:
    "Hoy, más del 25% de los pacientes en Estados Unidos son adultos, pero en nuestra práctica la proporción está más cerca de 50:50. Nuestros pacientes tienen edades de 7 a 77 años. Algunos adultos vienen buscando tratamiento por primera vez y otros quieren corregir dientes que se han movido con el tiempo. Sea cual sea tu razón, estamos aquí para ayudarte a obtener dientes hermosos y derechos que te mantendrán sonriendo por muchos años.",
  straightenTitle: "¿Por qué enderezar tus dientes?",
  straightenList: [
    "Una sonrisa con más confianza",
    "Mejor autoestima",
    "Los dientes derechos son más fáciles de mantener limpios",
    "Menor probabilidad de desarrollar enfermedad de las encías, mal aliento, desgaste dental y posible pérdida de dientes",
  ],
  straightenP1:
    "Desde una perspectiva de salud, los dientes derechos son más fáciles de mantener limpios que los dientes apiñados y superpuestos, lo que disminuye las probabilidades de desarrollar enfermedad de las encías, mal aliento, desgaste dental y posible pérdida de dientes.",
  straightenP2:
    "Desde un punto de vista cosmético, los dientes derechos pueden generar una sonrisa con más confianza y una mejor autoestima, lo que también puede mejorar el rendimiento en el trabajo o en la escuela. Sin importar la razón, todas las opciones de tratamiento se presentan en la primera cita, y tú decides qué funciona mejor para ti.",
  whyIntro: "Muchos adultos eligen Invisalign porque…",
  whyTitle: "¿Por qué elegir Invisalign?",
  whyList: [
    "Es más cosmético – no visible en fotos",
    "Sin restricciones de comida – se los quita para comer",
    "Más fácil de limpiar – el cepillado y el uso del hilo dental no es un problema",
    "Más cómodo – sin alambres ni brackets",
  ],
  responsibilitiesTitle: "Responsabilidades de Invisalign",
  responsibilitiesP1:
    "Es importante usar tus alineadores durante al menos 22 horas cada día para que hagan su trabajo y enderecen tus dientes. Pero, con Invisalign, puedes quitarte los alineadores para ocasiones especiales.",
  responsibilitiesP2:
    "Al igual que con los brackets tradicionales, generalmente necesitarás usar un retenedor después del tratamiento con Invisalign para mantener tu nueva alineación y tu nueva sonrisa con confianza.",
  responsibilitiesP3:
    "Es posible que Invisalign no se recomiende para algunos problemas complejos de alineación o mordida, o para quienes tienen puentes; la doctora puede aconsejarte sobre qué opciones son mejores para ti.",
  responsibilitiesP4:
    "Programe su consulta gratuita en nuestra oficina de Miami, FL para saber si Invisalign es adecuado para usted.",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

function InlineLink({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  return (
    <Link to={to} className={styles.inlineLink}>
      {children}
    </Link>
  );
}

export function InvisalignPage() {
  const getLocalization = useLocalization(localized);
  const { locale } = useContext(LocalizationContext);
  const isSpanish = locale === Locale.ES;

  return (
    <>
      <HeroStyle2
        heroImageUrl="/invisalign.jpg"
        mobileBackgroundPosition="62% 0%"
        title={getLocalization("title")}
        backgroundPosition="top"
      />

      <Flex
        className={`section ${styles.mainContentSection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex
          direction="column"
          className={styles.contentSection}
          gap="8"
          p="5"
        >
          {/* Basics Section */}
          <Flex direction="column" gap="6">
            <Heading as="h2" className={styles.heading}>
              {getLocalization("heading")}
            </Heading>
            <Text size="5">
              <InlineLink to="/invisalign-miami-fl">Invisalign</InlineLink>®{" "}
              {isSpanish
                ? getLocalization("basicsP1").replace(/^Invisalign® /, "")
                : getLocalization("basicsP1").replace(/^Invisalign® /, "")}
            </Text>
            <Text size="5">
              {isSpanish ? "Cuando eliges a " : "When you choose "}
              <InlineLink to="/">Miami Orthodontic Specialists</InlineLink>
              {isSpanish
                ? " como tu proveedor de Invisalign, la Dra. García-Paul tomará impresiones de tus dientes y luego trabajará con un software computarizado 3D para planificar el movimiento dental. "
                : " as your Invisalign provider, Dr. Garcia-Paul will take impressions of your teeth and then work with 3-D computer-based software to plan out your tooth movement. "}
              <InlineLink to="/invisalign-miami-fl">Invisalign</InlineLink>
              {isSpanish
                ? " es una alternativa estética a los métodos tradicionales de tratamiento de ortodoncia."
                : " is an esthetic alternative to traditional orthodontic treatment methods."}
            </Text>
            <Text size="5">
              {isSpanish ? "Llama para programar tu primera " : "Call to schedule your first "}
              <InlineLink to="/free-consult">
                {isSpanish ? "consulta gratuita" : "free consultation"}
              </InlineLink>
              {isSpanish
                ? " hoy y saber si los alineadores "
                : " appointment today to see if "}
              <InlineLink to="/invisalign-miami-fl">Invisalign</InlineLink>
              {isSpanish
                ? " pueden ayudarte a perfeccionar tu sonrisa."
                : " aligners can help you perfect your smile."}
            </Text>
          </Flex>

          <Box width="100%">
            <OptimizedImage
              src="/why-choose-us-2.jpg"
              alt="Miami FL Invisalign"
              width="100%"
              height="auto"
            />
          </Box>

          {/* Cost Section */}
          <Flex direction="column" gap="6">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("costTitle")}
            </Heading>
            {isSpanish ? (
              <Text size="5">
                Varios factores determinan el costo del tratamiento de
                Invisalign en{" "}
                <InlineLink to="/">Miami Orthodontic Specialists</InlineLink>.
                En la mayoría de los casos, no cobramos más por el{" "}
                <InlineLink to="/our-treatments">tratamiento</InlineLink> con{" "}
                <InlineLink to="/invisalign-miami">Invisalign</InlineLink> que
                por los <InlineLink to="/miami-braces">brackets</InlineLink> u
                otro tipo de{" "}
                <InlineLink to="/our-treatments">tratamiento</InlineLink>. El
                costo exacto del{" "}
                <InlineLink to="/our-treatments">tratamiento</InlineLink>{" "}
                depende de los problemas con los dientes y la mordida, y de
                cuánto tiempo tomará el tratamiento.
              </Text>
            ) : (
              <Text size="5">
                Several factors determine the cost of Invisalign treatment at{" "}
                <InlineLink to="/">Miami Orthodontic Specialists</InlineLink>.
                In most cases, we do not charge more for{" "}
                <InlineLink to="/invisalign-miami">Invisalign</InlineLink>{" "}
                <InlineLink to="/our-treatments">treatment</InlineLink> than we
                would for <InlineLink to="/miami-braces">braces</InlineLink> or
                another{" "}
                <InlineLink to="/our-treatments">treatment</InlineLink> type.
                The exact{" "}
                <InlineLink to="/our-treatments">treatment</InlineLink> cost
                depends on the problems with the teeth and bite and how long the
                treatment is going to take.
              </Text>
            )}
            <Text size="5">
              {isSpanish
                ? "Por eso ofrecemos una consulta inicial gratuita, para que la Dra. García-Paul pueda evaluarte y podamos explicarte el "
                : "This is why we offer a free initial consultation – so that you can be evaluated by Dr. Garcia-Paul and we can explain the "}
              <InlineLink to="/our-treatments">
                {isSpanish ? "tratamiento" : "treatment"}
              </InlineLink>
              {isSpanish
                ? " que te ayudará a lograr la sonrisa de tus sueños."
                : " that will help you achieve your dream smile."}
            </Text>
          </Flex>

          {/* Affordable Treatment Section */}
          <Flex direction="column" gap="6">
            <Heading as="h4" className={styles.subsectionHeading}>
              {getLocalization("affordableTitle")}
            </Heading>
            <Text size="5">
              {isSpanish ? "En " : "At "}
              <InlineLink to="/">Miami Orthodontic Specialists</InlineLink>
              {isSpanish
                ? ", hacemos todo lo posible para que Invisalign sea una opción de tratamiento asequible. Sabemos que unos dientes alineados son una inversión en tu salud y en tu futuro."
                : ", we do everything possible to make Invisalign an affordable treatment option. We know that straight teeth are an investment in your health and in your future."}
            </Text>
          </Flex>

          {/* Payment Options Section */}
          <Flex direction="column" gap="6">
            <Heading as="h4" className={styles.subsectionHeading}>
              {getLocalization("paymentTitle")}
            </Heading>
            <Flex asChild direction="column" gap="2">
              <ul>
                {getLocalization("paymentList").map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Flex>
          </Flex>

          {/* Process Section */}
          <Flex direction="column" gap="6">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("processTitle")}
            </Heading>
            <Text size="5">{getLocalization("processP1")}</Text>
            <Text size="5">{getLocalization("processP2")}</Text>
            <Text size="5">{getLocalization("processP3")}</Text>
            <Text size="5">
              <InlineLink to="/invisalign-miami">Invisalign</InlineLink>®{" "}
              {getLocalization("processP4").replace(/^Invisalign® /, "")}
            </Text>
            <Text size="5">{getLocalization("processP5")}</Text>
            <Text size="5">
              {isSpanish
                ? "Obtendrás una gran sonrisa sin un "
                : "You’ll get a great smile without a "}
              <InlineLink to="/our-treatments">
                {isSpanish ? "tratamiento" : "treatment"}
              </InlineLink>
              {isSpanish
                ? " que interfiera con tu vida diaria. Lo mejor de todo el proceso es que la mayoría de las personas ni siquiera sabrán que estás enderezando tus dientes. Obtén más información en el sitio de "
                : " that gets in the way of your daily life. The best part about the whole process is that most people won’t even know you’re straightening your teeth. See more at the "}
              <InlineLink to="/invisalign-miami">Invisalign</InlineLink>
              {isSpanish ? "." : " site."}
            </Text>
            <Text size="5">{getLocalization("processP7")}</Text>
          </Flex>

          {/* Differences Section */}
          <Flex direction="column" gap="6">
            <Heading as="h4" className={styles.subsectionHeading}>
              {getLocalization("differencesTitle")}
            </Heading>
            <Text size="5">{getLocalization("differencesP1")}</Text>
            <Text size="5">{getLocalization("differencesP2")}</Text>
            <Text size="5">{getLocalization("differencesP3")}</Text>
          </Flex>

          {/* Adults Section */}
          <Flex direction="column" gap="6">
            <Heading as="h4" className={styles.subsectionHeading}>
              {getLocalization("adultsTitle")}
            </Heading>
            <Text size="5">
              {isSpanish ? "¡Nos enorgullece ser el Proveedor Preferido de " : "We are proud to be the "}
              <InlineLink to="/invisalign-miami">
                {isSpanish ? "Invisalign en Miami" : "Miami Invisalign"}
              </InlineLink>
              {isSpanish ? "!" : " Preferred Provider!"}
            </Text>
            <Text size="5">{getLocalization("adultsP2")}</Text>
            <Text size="5">{getLocalization("adultsP3")}</Text>
            <Text size="5">{getLocalization("adultsP4")}</Text>
            <Text size="5">{getLocalization("adultsP5")}</Text>

            <Heading as="h5" className={styles.subheading}>
              {getLocalization("straightenTitle")}
            </Heading>
            <Flex asChild direction="column" gap="2">
              <ul>
                {getLocalization("straightenList").map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Flex>
            <Text size="5">{getLocalization("straightenP1")}</Text>
            <Text size="5">{getLocalization("straightenP2")}</Text>
            <Text size="5">
              {isSpanish ? "Muchos adultos eligen " : "Many adults choose "}
              <InlineLink to="/invisalign-miami">Invisalign</InlineLink>
              {isSpanish ? " porque…" : " because…"}
            </Text>
            <Flex asChild direction="column" gap="2">
              <ul>
                {getLocalization("whyList").map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Flex>
          </Flex>

          {/* Responsibilities Section */}
          <Flex direction="column" gap="6">
            <Heading as="h4" className={styles.subsectionHeading}>
              {getLocalization("responsibilitiesTitle")}
            </Heading>
            <Text size="5">{getLocalization("responsibilitiesP1")}</Text>
            <Text size="5">{getLocalization("responsibilitiesP2")}</Text>
            <Text size="5">{getLocalization("responsibilitiesP3")}</Text>
            <Text size="5">
              {isSpanish
                ? "Programa tu "
                : "Schedule your free "}
              <InlineLink to="/free-consult">
                {isSpanish ? "consulta gratuita" : "consultation"}
              </InlineLink>
              {isSpanish
                ? " en nuestra oficina de Miami, FL para saber si Invisalign es adecuado para ti."
                : " at our Miami, FL office to learn if Invisalign is right for you."}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
